import { Component, Input, OnChanges } from '@angular/core';
import { CompanyMarginByClientData, MarginByClientRow } from '../../../interfaces/metrics';

export interface RankedMarginRow extends MarginByClientRow {
  marginEuros: number | null;   // rounded to the nearest 1,000, DISPLAY ONLY (see marginEurosExact)
  isLoss: boolean;              // true sign, from the UNROUNDED figure; never derive this from marginEuros
  underFloor: boolean;          // isLoss but |exact| < 500 rounded marginEuros away to 0; never show as €0
  barPct: number;
  posPct: number;
  negPct: number;
}

@Component({
  selector: 'bst-company-margin-by-client',
  templateUrl: './company-margin-by-client.component.html',
  styleUrls: ['./company-margin-by-client.component.scss'],
  standalone: false,
})
export class CompanyMarginByClientComponent implements OnChanges {
  @Input() data!: CompanyMarginByClientData;

  rankedRows: RankedMarginRow[] = [];
  hasNegative = false;

  // Precomputed once per data change (matches team-health-strip's convention):
  // rankedRows/hasNegative/bar percentages were getters re-entering each other on
  // every CD tick, rebuilding the whole table for nothing between real updates.
  ngOnChanges(): void {
    // Ranked by margin contribution in euros (revenue x marginPct), not by revenue: a
    // small, high-margin client can matter more than a big, thin one. Rows whose money
    // is suppressed (privacy folding, or no grant at all) carry marginEuros null and
    // sink to the bottom, in their original (revenue-ranked) order.
    //
    // Sort, sign and bar widths all read `exact` (the unrounded figure), never the
    // rounded marginEuros: rounding to the nearest 1,000 before sorting/sign-checking
    // can turn a real loss into a JS -0 (e.g. revenue 1,000 at -10% -> -0.1 rounds to
    // -0), which sorts as 0 and, since -0 < 0 is false in JS, is silently indistinguishable
    // from a genuine zero-margin row. Rounding is applied ONLY when building the display
    // value below.
    const rows = (this.data?.clients ?? []).map(r => ({ ...r, exact: this.marginEurosExact(r) }));
    rows.sort((a, b) => {
      if (a.exact === null) { return b.exact === null ? 0 : 1; }
      if (b.exact === null) { return -1; }
      return b.exact - a.exact;
    });

    // Zero-centred scale only kicks in once a loss is actually present, so the common
    // all-positive case keeps the simpler left-anchored 0..max bar.
    this.hasNegative = rows.some(r => r.exact !== null && r.exact < 0);
    const abs = rows.map(r => r.exact).filter((v): v is number => v !== null).map(v => Math.abs(v));
    const maxAbsMargin = Math.max(1, ...abs);   // 1 floor: avoids a /0 when every margin is exactly 0

    this.rankedRows = rows.map(({ exact, ...r }) => {
      const marginEuros = exact === null ? null : Math.round(exact / 1000) * 1000;
      const isLoss = exact !== null && exact < 0;
      return {
        ...r,
        marginEuros,
        isLoss,
        // A real loss can round away to 0 at the 1,000 step (any |exact| < 500): never
        // let a flagged loss render as the literal, contradictory "€0".
        underFloor: isLoss && marginEuros === 0,
        // Left-anchored 0..max bar, used only when no client is loss-making.
        barPct: exact === null ? 0 : Math.max(0, exact) / maxAbsMargin * 100,
        // Zero-centred case: each half of the track is 50% of the width, so a value at
        // maxAbsMargin fills its whole half.
        posPct: exact !== null && exact > 0 ? exact / maxAbsMargin * 50 : 0,
        negPct: exact !== null && exact < 0 ? -exact / maxAbsMargin * 50 : 0,
      };
    });
  }

  trackByRow(_index: number, row: RankedMarginRow): string {
    return row.kind + ':' + (row.name ?? '');
  }

  // Null propagates: a suppressed or unpriced row (revenue or marginPct null) must
  // never resolve to a bar or a euro figure, only the '-' placeholder. UNROUNDED: this
  // is the figure sort/sign/bar-width all use; marginPct is an INTEGER percent already
  // rounded by the resolver, so revenue * marginPct looks exact to the euro while
  // actually being off by hundreds of euros at real revenue scale (23% vs a true 23.4%
  // on 120,000 diverges by 480) - rounding to the nearest 1,000 stays a DISPLAY-only
  // step (see ngOnChanges) so it can never flip a real loss into a false "0". The
  // column header marks the displayed figure as approximate to match.
  private marginEurosExact(row: MarginByClientRow): number | null {
    return row.revenue !== null && row.marginPct !== null
      ? row.revenue * row.marginPct / 100
      : null;
  }
}
